import React from "react"
import Pill from "@irati/ui/src/Pill"
import {CelebrationTag} from "@irati/types"
import {Body, H3} from "@irati/ui/src/Typography"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import FormattedMessage from "@irati/utils/lang"
import {injectIntl, InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import adminTracking from "@irati-tracking/admin"
import Expander from "@irati/ui/src/Expander"
import {useRecognitionAutomationsQuery} from "@irati/recognition-automation/hooks"
import {AUTOMATION_BASE, AUTOMATION_CREATE} from "../../routes/routeConfig"
import {AutomationItemContainer, AutomationItemImage, MainContent} from "./styled"
import AutomationCountryList from "./AutomationCountryList"
import {useBillingUnitsQuery} from "@irati/billing/hooks/bu"
import authentication from "@irati/auth"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"

interface AutomationItemProps extends InjectIntlProps {
  automation: CelebrationTag
}
const MainActionButtonWrapper = ({children, hasTooltip}: {children: JSX.Element; hasTooltip: boolean}) => {
  if (hasTooltip) {
    return (
      <Tooltip
        tooltipChildren={
          <Body>
            <FormattedMessage id="admin.components.businessUnits.countries.notAvailable" />
          </Body>
        }
        placement={OverlayPlacement.Bottom}>
        {children}
      </Tooltip>
    )
  }
  return children
}
const AutomationItem = (props: AutomationItemProps) => {
  const {automation, formatMessage} = props
  const onAutomationCreate = () => adminTracking.automationClick(automation.name)
  const {data: billingUnits} = useBillingUnitsQuery(
    {
      businessId: authentication.business().uuid
    },
    {
      fetchPolicy: "cache-and-network"
    }
  )
  const {data, loading} = useRecognitionAutomationsQuery(
    {
      occasionId: automation.uuid
    },
    {
      skip: automation.isComingSoon
    }
  )
  const businessUnitOptionsLeft = billingUnits?.filter(({teamId}) => !data?.some(({regionId}) => regionId === teamId))
  const recognitionAutomations = automation.isComingSoon ? [] : data
  const setupAutomationLabel = recognitionAutomations?.length ? "admin.automation.btn.addNewAutomation" : "admin.automation.btn.setUpNew"

  return (
    <AutomationItemContainer>
      <Expander
        fullWidth
        HeaderLabel={({children}) => children}
        showArrow={!!recognitionAutomations?.length}
        showText={
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <MainContent disabled={automation.isComingSoon}>
              <AutomationItemImage src={automation.icon?.href} alt={automation.icon?.alt} />
              <Flex direction="column">
                <H3>{automation.name}</H3>
                <Body small>{automation.label}</Body>
              </Flex>
            </MainContent>
            {!automation.isComingSoon && !loading && (
              <MainActionButtonWrapper hasTooltip={!businessUnitOptionsLeft?.length}>
                <ButtonRouterLink
                  onClick={onAutomationCreate}
                  disabled={!businessUnitOptionsLeft?.length}
                  to={`${AUTOMATION_BASE}${AUTOMATION_CREATE.replace(":uuid", automation.uuid!)}`}>
                  <FormattedMessage id={setupAutomationLabel} />
                </ButtonRouterLink>
              </MainActionButtonWrapper>
            )}
            {automation.isComingSoon && !loading && (
              <Pill
                lineHeight={mainTheme.spacing.three}
                fontSize={mainTheme.spacing.oneAndHalf}
                text={formatMessage({id: "platform.label.comingSoon"})}
                backgroundColor={mainTheme.colors.greys.grey50}
                isWhiteText
              />
            )}
          </Flex>
        }>
        {!automation.isComingSoon && <AutomationCountryList recognitionAutomations={recognitionAutomations!} />}
      </Expander>
    </AutomationItemContainer>
  )
}

export default injectIntl(AutomationItem)
